import { SETUP_CHECKLIST_ITEM_KEYS, type SetupChecklistItemKey, type SetupChecklistStatus } from '@ruta/shared';
import type { NavigationAction } from '@/contexts/ChatContext';

export interface SetupItemMeta {
  key: SetupChecklistItemKey;
  /** Checklist line label; completed items render struck through. */
  label: string;
  /** In-app destination for this step's navigation action. */
  path: string;
  /** Button label when this step is the primary next action. */
  actionLabel: string;
  /**
   * Owner can mark this done without doing it — a one-person shop does not invite
   * crew, and a company that does all the work itself does not set up subcontractors.
   */
  skippable?: boolean;
  /**
   * Where the checklist points when this line is picked: the page, the exact control
   * on it (`data-tour` anchors, first one on the page wins), and the one sentence that
   * says what to do there.
   */
  spotlight: { route: string; target: string[]; instruction: string };
}

// Ordered import-first (mirrors SETUP_CHECKLIST_ITEM_KEYS). Kept in this order so
// the rendered checklist and the "next step" selection agree.
export const SETUP_ITEMS: SetupItemMeta[] = [
  {
    key: 'importCustomers', label: 'Import your customers', path: '/settings?tab=data', actionLabel: 'Import your data',
    spotlight: {
      route: '/settings?tab=data',
      target: ['[data-tour="settings-import"]'],
      instruction: 'Drop your customer list in here — a spreadsheet or an export from your old system. Nothing is saved until you approve what it found.',
    },
  },
  {
    key: 'activatePayments', label: 'Activate payments', path: '/settings?tab=billing', actionLabel: 'Activate payments',
    spotlight: {
      route: '/settings?tab=billing',
      target: ['[data-tour="settings-payments"]'],
      instruction: 'Finish the merchant application here — EIN, bank account, a few details about the business. Customers can pay by card or bank as soon as it is approved.',
    },
  },
  {
    key: 'inviteCrewMembers', label: 'Invite your crew members', path: '/team?tab=members', actionLabel: 'Invite your crew', skippable: true,
    spotlight: {
      route: '/team?tab=members',
      target: ['[data-tour="team-invite"]'],
      instruction: 'Press Invite and enter a name and mobile number. They get a text with the crew app and a code to sign in.',
    },
  },
  {
    key: 'organizeCrews', label: 'Organize your crews', path: '/team?tab=crews', actionLabel: 'Organize your crews',
    spotlight: {
      route: '/team?tab=crews',
      target: ['[data-tour="team-create-crew"]'],
      instruction: 'Press Create Crew, name it, and pick who is on it. Visits are assigned to crews, so this is what the schedule works from.',
    },
  },
  {
    key: 'reviewServices', label: 'Review your services & pricing', path: '/service-templates', actionLabel: 'Review your services',
    spotlight: {
      route: '/service-templates',
      target: ['[data-tour="services-list"]', '[data-tour="nav-service-templates"]'],
      instruction: 'These are the services you offer and what each one costs. Open each one to check its price and what it includes, so quotes and the schedule have the right numbers to pick from.',
    },
  },
  {
    key: 'setUpSubcontractors', label: 'Set up subcontractors', path: '/team?tab=subcontractors', actionLabel: 'Set up subcontractors', skippable: true,
    spotlight: {
      route: '/team?tab=subcontractors',
      target: ['[data-tour="team-add-subcontractor"]'],
      instruction: 'Press New Company for each subcontractor you send work to. Skip this if you do all the work yourselves.',
    },
  },
  {
    key: 'addCompanyPhone', label: 'Add the company phone number', path: '/settings?tab=company', actionLabel: 'Add the company phone',
    spotlight: {
      route: '/settings?tab=company',
      target: ['[data-tour="settings-company-phone"]'],
      instruction: 'Type the line customers call in here. Texts and reminders to customers go out from it.',
    },
  },
  {
    key: 'addYourMobile', label: 'Add your mobile number', path: '/user-settings', actionLabel: 'Add your mobile',
    spotlight: {
      route: '/user-settings',
      target: ['[data-tour="user-mobile"]'],
      instruction: 'Type your own mobile number here. It is how the crew app signs you in, and where your alerts go.',
    },
  },
];

// Guard against the ordered metadata drifting from the shared source of truth.
// (Both are static, so this is a build-time-ish invariant surfaced at module load.)
if (SETUP_ITEMS.length !== SETUP_CHECKLIST_ITEM_KEYS.length) {
  throw new Error('setupIntroMessage: SETUP_ITEMS is out of sync with SETUP_CHECKLIST_ITEM_KEYS');
}

export interface SetupIntroContent {
  content: string;
  navigationActions: NavigationAction[];
  suggestions: Array<{ label: string; prompt: string }>;
}

/**
 * Build the fully-scripted Ruta AI welcome for the owner's setup checklist (RUT-2955).
 *
 * Pure so the gate logic stays testable: no Date, ids, or React. The gate wraps the
 * result in a ChatMessage. Copy leads with data import while customers aren't in yet,
 * and the primary navigation action always points at the most relevant next step.
 */
export function buildSetupIntro(checklist: SetupChecklistStatus): SetupIntroContent {
  const items = checklist.items;
  const importDone = items.importCustomers;

  const intro = importDone
    ? "Welcome to Ruta! I'm your setup assistant. You've made a great start — here's what's left to get your operation fully up and running. Tap an item below and I'll walk you through it."
    : "Welcome to Ruta! I'm your setup assistant, and the best first move is getting your customers into the system. Once your data's in, everything else — payments, invites, crews — falls into place. Here's your setup checklist:";

  const list = SETUP_ITEMS.map((it) => `- ${items[it.key] ? `~~${it.label}~~` : it.label}`).join('\n');
  const content = `${intro}\n\n${list}`;

  // While customers aren't imported, always steer to data import first; once that's
  // done, link the first still-incomplete step. `active` guarantees at least one is
  // incomplete, but we stay defensive and emit no action if somehow all are done.
  const target = importDone ? SETUP_ITEMS.find((it) => !items[it.key]) : SETUP_ITEMS[0];
  const navigationActions: NavigationAction[] = target
    ? [{ label: target.actionLabel, path: target.path, state: {}, variant: 'primary' }]
    : [];

  const suggestions = [
    { label: 'What should I do first?', prompt: 'What should I do first to set up my account?' },
    { label: 'Help me set up my crews', prompt: 'Help me set up my crews.' },
    { label: "I'm done with setup", prompt: "I'm done with setup." },
  ];

  return { content, navigationActions, suggestions };
}
